import type { OperationContext } from 'urql';
import clsx from 'clsx';
import React, { useMemo, useState } from 'react';

import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { SecondaryButtonV2 } from '@fragment/ui/src/components/Button/SecondaryButtonV2/SecondaryButtonV2';
import { AccountTypeIcon } from '@fragment/ui/src/components/Icon/AccountTypeIcon/AccountTypeIcon';
import { InlineError } from '@fragment/ui/src/components/InlineError/InlineError';
import { LoadingText } from '@fragment/ui/src/components/LoadingText';
import { POSTHOG_MASK_CLASS } from '@fragment/ui/src/constants';

import { LedgerAccountTypes } from '../../../../hooks/customerApi';
import { useGetWorkspacePaymentAccountsQuery } from '../../../../hooks/internalApi';
import { usePaymentAccountVerificationToken } from '../../../../hooks/usePaymentAccountVerificationToken';
import { getResultOrError } from '../../../../utils/errors';
import { Summary } from '../../../Summary';
import {
  PaymentAccountVerificationPanel,
  type PaymentAccountVerificationSession,
} from './PaymentAccountVerificationPanel';

type Props = {
  workspaceId: string;
  context: Partial<OperationContext>;
  hasAccessToken: boolean;
  canVerify: boolean;
  onError: (message: string) => void;
};

export const PaymentAccountSettingsList = ({
  workspaceId,
  context,
  hasAccessToken,
  canVerify,
  onError,
}: Props) => {
  // urql requires stable query variables between renders.
  const variables = useMemo(() => ({ workspaceId }), [workspaceId]);
  const [{ data, error, fetching }, refetch] =
    useGetWorkspacePaymentAccountsQuery({
      variables,
      context,
      pause: !hasAccessToken,
    });
  const workspace = getResultOrError(data?.workspace, error);
  const { createVerificationToken, fetching: isCreatingToken } =
    usePaymentAccountVerificationToken({ workspaceId, context });
  const [verification, setVerification] = useState<
    { ledgerId: string; session: PaymentAccountVerificationSession } | undefined
  >(undefined);
  const refreshAccounts = () => refetch({ requestPolicy: 'network-only' });
  const startVerification = (ledgerId: string) => {
    createVerificationToken(ledgerId)
      .then((session) => setVerification({ ledgerId, session }))
      .catch((cause: unknown) => {
        onError(
          cause instanceof Error
            ? cause.message
            : 'Unable to start verification.'
        );
      });
  };

  const renderAccounts = () => {
    if (fetching && !data)
      return <LoadingText text="Loading payment accounts" />;
    if (workspace.isError || !workspace.result.paymentAccounts)
      return (
        <div role="alert" className="flex flex-col gap-f2">
          <InlineError>Unable to load payment accounts</InlineError>
          <SecondaryButtonV2 disabled={fetching} onClick={refreshAccounts}>
            Retry
          </SecondaryButtonV2>
        </div>
      );
    const accounts = workspace.result.paymentAccounts;
    if (accounts.length === 0)
      return (
        <p>
          You have no payment accounts. Deploy one to begin processing payments
        </p>
      );
    if (accounts.every((account) => account.verification?.isVerified))
      return <p>Your account has been successfully verified.</p>;
    if (accounts.length === 1) {
      const [account] = accounts;
      return (
        <div className="flex flex-col items-start gap-f4">
          <p>
            Your account needs to be verified before you can process payments.
          </p>
          {!account.verification && (
            <p className="text-main-500">Not provisioned yet</p>
          )}
          {canVerify ? (
            <PrimaryButtonV2
              disabled={!account.verification || isCreatingToken}
              onClick={() => startVerification(account.ledgerId)}
            >
              Verify
            </PrimaryButtonV2>
          ) : (
            <p className="text-main-500">
              Ask an admin to verify your payment account
            </p>
          )}
        </div>
      );
    }
    return (
      <ul className="flex flex-col gap-f4" aria-label="Payments KYC">
        {workspace.result.paymentAccounts.map((account) => (
          <li
            key={account.ledgerId}
            // eslint-disable-next-line @fragment/tailwind-grid -- Explicit columns keep the action sized to its content.
            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-f4 border-b border-main-200 pb-f4"
          >
            <div className="min-w-0">
              <Summary.Table
                values={[
                  {
                    header: 'Ledger',
                    truncate: true,
                    value: (
                      <div className="flex min-w-0 flex-col gap-f1">
                        <span className={clsx(POSTHOG_MASK_CLASS, 'truncate')}>
                          {account.ledgerName}
                        </span>
                        <div className="flex min-w-0 items-center gap-f1">
                          <span
                            className="shrink-0"
                            aria-label="Ledger account"
                          >
                            <AccountTypeIcon type={LedgerAccountTypes.Asset} />
                          </span>
                          <span
                            className={clsx(POSTHOG_MASK_CLASS, 'truncate')}
                          >
                            {account.accountPath}
                          </span>
                        </div>
                      </div>
                    ),
                  },
                ]}
              />
              {!account.verification && (
                <div className="mt-f2 text-main-500">Not provisioned yet</div>
              )}
            </div>
            {account.verification?.isVerified && (
              <span className="rounded border border-main-300 px-f2 text-main-500">
                Verified
              </span>
            )}
            {!account.verification?.isVerified && canVerify && (
              <SecondaryButtonV2
                disabled={!account.verification || isCreatingToken}
                onClick={() => startVerification(account.ledgerId)}
              >
                Verify
              </SecondaryButtonV2>
            )}
            {!account.verification?.isVerified && !canVerify && (
              <div className="flex flex-col gap-f1 text-main-500">
                <span>Not verified</span>
                <span>Ask an admin to verify your payment account</span>
              </div>
            )}
          </li>
        ))}
      </ul>
    );
  };

  if (canVerify && verification)
    return (
      <PaymentAccountVerificationPanel
        key={verification.ledgerId}
        {...verification}
        createVerificationToken={createVerificationToken}
        onBack={() => setVerification(undefined)}
        onCompleted={() => {
          setVerification(undefined);
          refreshAccounts();
        }}
      />
    );
  return renderAccounts();
};
